import type {MouseEvent as ReactMouseEvent, ReactNode} from "react";
import {Modal} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import MatchBox from "./MatchBox";
import {formatDate, playerToString} from "../../util/StringUtils";
import ReactCountryFlag from "react-country-flag";
import {useMatchDetail} from "../../hooks/queries/useMatch";

interface MatchDetailModalProps {
    isOpen: boolean;
    matchId: number | null;
    onClose: () => void;
}

interface ComparisonRowOptions {
    highlightBetter?: boolean;
    higherIsBetter?: boolean;
    isPercentage?: boolean;
    isForm?: boolean;
}

type ComparisonValue = number | string | null | undefined;

function MatchDetailModal({ isOpen, matchId, onClose }: MatchDetailModalProps) {
    const { t } = useTranslation();
    const { data: matchDetail, isFetching } = useMatchDetail(isOpen ? matchId : null);
    const loading = isFetching && !matchDetail;

    const getComparisonRow = (
        label: ReactNode,
        p1Value: ComparisonValue,
        p2Value: ComparisonValue,
        options: ComparisonRowOptions = {},
    ) => {
        const { highlightBetter = false, higherIsBetter = true, isPercentage = false, isForm = false } = options;

        let p1Class = "";
        let p2Class = "";

        if (highlightBetter && p1Value !== p2Value && p1Value != null && p2Value != null) {
            const p1Better = higherIsBetter ? p1Value > p2Value : p1Value < p2Value;
            p1Class = p1Better ? "win" : "loss";
            p2Class = p1Better ? "loss" : "win";
        }

        const formatValue = (val: ComparisonValue): ReactNode => {
            if (val == null || val === "") return "-";
            if (isPercentage && typeof val === "number") return `${(val * 100).toFixed(0)}%`;
            if (typeof val === "number" && !Number.isInteger(val)) return val.toFixed(2);
            return val;
        };

        const renderFormString = (form: ComparisonValue): ReactNode => {
            if (!form || typeof form !== "string" || form === "") return <span style={{ color: 'var(--text-muted)' }}>-</span>;
            return (
                <span>
                    {form.split("").map((char: string, idx: number) => (
                        <span key={idx} className={char === "W" ? "win" : "loss"} style={{fontWeight: "bold"}}>
                            {char}&nbsp;
                        </span>
                    ))}
                </span>
            );
        };

        return (
            <div className="comparison-row d-flex align-items-center py-2" style={{borderBottom: "1px solid var(--border-color)"}}>
                <div className={`text-end flex-grow-1 ${p1Class}`} style={{minWidth: "80px"}}>
                    {isForm ? renderFormString(p1Value) : formatValue(p1Value)}
                </div>
                <div className="text-center px-3" style={{minWidth: "120px", color: "var(--text-muted)", fontSize: "0.85em"}}>
                    {label}
                </div>
                <div className={`text-start flex-grow-1 ${p2Class}`} style={{minWidth: "80px"}}>
                    {isForm ? renderFormString(p2Value) : formatValue(p2Value)}
                </div>
            </div>
        );
    };

    const getVersusHeader = () => {
        if (!matchDetail) return null;
        const p1 = matchDetail.match.playerOne;
        const p2 = matchDetail.match.playerTwo;

        return (
            <div className="versus-header d-flex align-items-center py-3 mb-3" style={{borderBottom: "2px solid var(--border-color)"}}>
                <div className="text-center flex-grow-1">
                    <Link to={"/players/" + p1.id} style={{ textDecoration: 'none', fontSize: "0.95em", color: 'var(--accent)' }} onClick={onClose}>
                        <ReactCountryFlag countryCode={p1.nationality ?? "UN"} svg title={p1.nationality ?? "UN"} className="flag me-2" />
                        {playerToString(p1, false, true)}
                    </Link>
                </div>
                <div className="text-center px-3" style={{fontSize: "1.1em", fontWeight: "bold", color: "var(--text-muted)"}}>
                    VS
                </div>
                <div className="text-center flex-grow-1">
                    <Link to={"/players/" + p2.id} style={{ textDecoration: 'none', fontSize: "0.95em", color: 'var(--accent)' }} onClick={onClose}>
                        <ReactCountryFlag countryCode={p2.nationality ?? "UN"} svg title={p2.nationality ?? "UN"} className="flag me-2" />
                        {playerToString(p2, false, true)}
                    </Link>
                </div>
            </div>
        );
    };

    const getComparisonStats = () => {
        if (!matchDetail) return null;
        const stats = matchDetail.comparisonStats;
        const hasH2H = stats.playerOneH2HWins > 0 || stats.playerTwoH2HWins > 0;

        return (
            <div className="comparison-stats mb-4">
                {getVersusHeader()}

                {getComparisonRow(t("winProbability"), stats.playerOneWinProbability, stats.playerTwoWinProbability,
                    { highlightBetter: true, higherIsBetter: true, isPercentage: true })}

                {getComparisonRow(t("elo"), stats.playerOneElo, stats.playerTwoElo,
                    { highlightBetter: true, higherIsBetter: true })}

                {getComparisonRow(t("currentForm"), stats.playerOneForm, stats.playerTwoForm, { isForm: true })}

                {getComparisonRow(t("matchesPlayed"), stats.playerOneTotalMatches, stats.playerTwoTotalMatches,
                    { highlightBetter: true, higherIsBetter: true })}

                {getComparisonRow(t("avgRating"), stats.playerOneAvgRating, stats.playerTwoAvgRating,
                    { highlightBetter: true, higherIsBetter: true })}

                <div className="mt-4 mb-2 text-center" style={{color: "var(--text-muted)", fontSize: "0.85em", textTransform: "uppercase"}}>
                    {t("headToHead")}
                </div>

                {hasH2H ? (
                    <>
                        {getComparisonRow(t("matchesWon"), stats.playerOneH2HWins, stats.playerTwoH2HWins,
                            { highlightBetter: true, higherIsBetter: true })}

                        {getComparisonRow(t("setsWon"), stats.playerOneH2HSetsWon, stats.playerTwoH2HSetsWon,
                            { highlightBetter: true, higherIsBetter: true })}

                        {getComparisonRow(t("pointsWon"), stats.playerOneH2HPointsWon, stats.playerTwoH2HPointsWon,
                            { highlightBetter: true, higherIsBetter: true })}

                        {getComparisonRow(t("eloGained"), stats.playerOneH2HEloGained, stats.playerTwoH2HEloGained,
                            { highlightBetter: true, higherIsBetter: true })}

                        {getComparisonRow(t("lastVictory"),
                            stats.playerOneLastVictoryDate ? formatDate(stats.playerOneLastVictoryDate) : null,
                            stats.playerTwoLastVictoryDate ? formatDate(stats.playerTwoLastVictoryDate) : null
                        )}
                    </>
                ) : (
                    <div className="text-center py-3" style={{ color: 'var(--text-muted)' }}>
                        {t("noHeadToHeadHistory")}
                    </div>
                )}
            </div>
        );
    };

    const getH2HMatchHistory = () => {
        if (!matchDetail || !matchDetail.headToHeadMatches || matchDetail.headToHeadMatches.length === 0) return null;

        return (
            <div className="h2h-history mt-4">
                <h6 className="mb-3" style={{ color: 'var(--text-primary)' }}>{t("previousMatches")}</h6>
                {matchDetail.headToHeadMatches.slice(0, 5).map(match => (
                    <MatchBox variant="profile" key={"h2h-match-" + match.id} match={match} showDetailButton={false} hideStatus={true} />
                ))}
                {matchDetail.headToHeadMatches.length > 5 && (
                    <div className="text-center mt-2" style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>
                        +{matchDetail.headToHeadMatches.length - 5} more matches
                    </div>
                )}
            </div>
        );
    };

    const stopPropagation = (e: ReactMouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" centered onClick={stopPropagation}>
            <Modal.Header
                closeButton
                onClick={stopPropagation}
                style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)'
                }}
            >
                <Modal.Title style={{ fontSize: '1.1rem' }}>{t("matchDetail")}</Modal.Title>
            </Modal.Header>
            <Modal.Body
                onClick={stopPropagation}
                style={{
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    maxHeight: '70vh',
                    overflowY: 'auto'
                }}
            >
                {loading ? (
                    <div className="text-center py-4" style={{ color: 'var(--text-muted)' }}>
                        {t("loading")}
                    </div>
                ) : matchDetail ? (
                    <>
                        <div className="mb-4">
                            <MatchBox variant="profile" match={matchDetail.match} hideStatus={true} showDetailButton={false} />
                        </div>
                        {getComparisonStats()}
                        {getH2HMatchHistory()}
                    </>
                ) : null}
            </Modal.Body>
        </Modal>
    );
}

export default MatchDetailModal;
